import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  TextField,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Paper,
  Link as MuiLink,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import SEO from '../components/SEO';
import { trackEvent } from '../components/GoogleAnalytics';
import { generateFAQPageSchema, type FAQInput } from '../utils/schemaGenerators';

interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
  category: string;
}

const FAQPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPanels, setExpandedPanels] = useState<{ [key: string]: boolean }>({});

  // FAQ data organized by category (useMemo to prevent recreating on every render)
  const faqData: FAQItem[] = useMemo(
    () => [
      // GETTING STARTED
      {
        category: 'Getting Started',
        question: "How does the website know which state I'm in?",
        answer:
          "When you first visit the site, you can manually select your state using the State Selector dropdown in the navigation. Your state preference is saved in your browser, so you don't need to select it again when you return. This allows us to show you bills and resources specific to your location automatically.",
      },
      {
        category: 'Getting Started',
        question: 'Can I view bills from other states besides my own?',
        answer:
          'Yes! While the site automatically shows bills for your saved location, you can easily browse bills from any region. Use the State Selector in the navigation menu to change locations, or visit the full directory on any bill page to see how that bill appears across all regions.',
      },
      {
        category: 'Getting Started',
        question: "What if I don't see my state or need to change it?",
        answer:
          'Click the "Set Location" button in the navigation header or use the State Selector dropdown. Choose your state from the list, and it will be saved automatically in your browser for future visits.',
      },
      {
        category: 'Getting Started',
        question: 'Is this website only for people in certain states?',
        answer:
          'No! We have versions of our bills adapted for multiple countries and regions. Every advocate, regardless of location, can access region-specific bills, contact legislators, and use our advocacy tools.',
      },
      {
        category: 'Getting Started',
        question: 'Do I need to create an account to use the site?',
        answer:
          'No account is necessary. All bills, email templates, and resources are freely accessible without registration. Your state preference is saved locally in your browser for convenience.',
      },
      {
        category: 'Getting Started',
        question: 'Do you have bill proposals for areas outside the US?',
        answer: (
          <>
            Yes, but they're not available on the website. We have international channels where our team has worked with
            local advocates in international areas to craft changes to their bills.{' '}
            <MuiLink href="/join-the-team" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              Join the Movement
            </MuiLink>{' '}
            to become a member of the community and join your country's channel.
          </>
        ),
      },

      // ACCESSING BILLS
      {
        category: 'Accessing Bills',
        question: 'What are the 14 bills in the Family Court Reform Bill Stack?',
        answer: (
          <>
            The{' '}
            <MuiLink href="/family-court-reform-bills" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              14 bills
            </MuiLink>{' '}
            cover comprehensive family court reform: (1) A New Start at Equality (50/50 custody presumption), (2) Time
            Taken Time Back (restoring custody after false allegations), (3) Three Strikes Law (penalties for custody
            interference), (4) Justice Delayed is Justice Denied (mandatory timelines), (5) Cameras in Courtrooms
            (transparency), (6) Child Support Dual Calculation Reform, (7) Appealable Temporary Orders, (8) The Litigant
            Access and Economy Act (remote court access), (9) Reimbursement of Fees for Parental Alienation, (10) The
            Family Rights and Due Process Act, (11) Child Support Integrity Act (parentage verification and accountable
            enforcement), (12) Best Interest of the Child Definition Act (a clear, evidence-based custody standard),
            (13) Parent-Child Restoration Act (prompt restoration of custody or visitation and compensatory parenting
            time after allegations are unsubstantiated, with safeguards for necessary child protection), and (14)
            Judicial Accountability Referral Act (a referral pathway that brings family court judicial conduct before an
            independent review commission).
          </>
        ),
      },
      {
        category: 'Accessing Bills',
        question: 'How do I find bills specific to my state?',
        answer:
          "Once you've selected your state, the homepage and Bills page automatically show your state's versions of all available bills. You can also browse directly to any bill page, which will display your state's version by default.",
      },
      {
        category: 'Accessing Bills',
        question: 'How do I see all regions for a specific bill?',
        answer:
          'From any bill detail page, click the "Set Location" button positioned above the bill name, then click "Unassign Location" in the dialog. This removes your location filter and shows all regions for that bill, allowing you to click on any region to view that region\'s version.',
      },
      {
        category: 'Accessing Bills',
        question: 'What do the different bill status stages mean?',
        answer:
          'Bills have 3 status stages: (1) Not Started - bill has not yet been introduced to the legislature, (2) In Progress - bill has been introduced and is moving through the legislative process, and (3) Passed - bill has been enacted into law. The status indicator shows where each bill currently stands.',
      },
      {
        category: 'Accessing Bills',
        question: 'How often is bill status information updated?',
        answer:
          'Our team updates bill statuses in real-time as changes occur. Updates appear on the website within 5-10 minutes of being edited in our database, so you always have current information without needing to refresh the page.',
      },
      {
        category: 'Accessing Bills',
        question: 'What is a Legislator Pack and how can I access it?',
        answer: (
          <>
            A Legislator Pack is a comprehensive Word document containing three sections: the Legislator Letter (email
            template), Background and Need Statement (talking points), and Proposed Amended Legal Text (bill language).
            Click the "View Legislator Pack" button on any{' '}
            <MuiLink href="/family-court-reform-bills" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              bill page
            </MuiLink>{' '}
            to get your state-specific document that you can copy and paste from.
          </>
        ),
      },
      {
        category: 'Accessing Bills',
        question: 'Can I share bills on social media?',
        answer:
          'Yes! Every bill page has social sharing buttons for Twitter/X, Facebook, and LinkedIn. The shared post automatically includes the bill title and your state for maximum impact.',
      },

      // EMAIL TEMPLATES
      {
        category: 'Legislator Email Templates',
        question: 'Where can I find email templates to send to my legislator?',
        answer: (
          <>
            Email templates are included in the Legislator Pack for each bill. Access the Legislator Pack from any{' '}
            <MuiLink href="/family-court-reform-bills" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              bill page
            </MuiLink>
            , and you'll find a pre-written "Legislator Letter" section that you can copy and paste to send to your
            state representatives.
          </>
        ),
      },
      {
        category: 'Legislator Email Templates',
        question: "What's included in the Legislator Pack?",
        answer:
          "Each Legislator Pack contains three sections: (1) Legislator Letter - a ready-to-send email template, (2) Background and Need Statement - talking points to help you quickly pitch the bill, and (3) Proposed Amended Legal Text - the actual bill language that modifies your state's legal code.",
      },
      {
        category: 'Legislator Email Templates',
        question: 'Can I customize the Legislator Letter template?',
        answer:
          "Yes, you can personalize it as needed. The Legislator Letter is professionally written to focus on the bill's merits and legislative value. You can use it as-is or add your contact information and any relevant credentials. Copy the text to your email client.",
      },
      {
        category: 'Legislator Email Templates',
        question: 'How do I use the Background and Need Statement section?',
        answer:
          'The Background and Need Statement section teaches you how to quickly pitch the bill to legislators, staff, or other advocates. It includes key facts, statistics, and compelling reasons why the bill is needed. Use these talking points when emailing, calling, or meeting with your representatives.',
      },

      // MEETING REQUESTS
      {
        category: 'Meeting Requests',
        question: 'Can Robert Garza meet with my legislator?',
        answer: (
          <>
            Yes! If you've arranged or are planning a meeting with a state or federal legislator and would like Robert
            to participate, use the{' '}
            <MuiLink href="/legislator-meetings" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              Legislator Meeting Request
            </MuiLink>{' '}
            form. Robert can join by video call, phone, or in-person depending on the meeting format and his
            availability.
          </>
        ),
      },
      {
        category: 'Meeting Requests',
        question: 'What information do I need for a meeting request?',
        answer:
          "You'll need the legislator's name and role (State Senator, State Representative, U.S. Senator, or U.S. Representative), your preferred meeting type (video call or phone), proposed date and time, meeting access details (link for video calls), and which bills you want to discuss.",
      },
      {
        category: 'Meeting Requests',
        question: 'What types of meetings can Robert participate in?',
        answer:
          'Robert or a team member can join video calls (Zoom, Teams, etc.), phone conferences, or in-person meetings depending on scheduling and location. Select your preferred format when submitting the meeting request form.',
      },

      // UNDERSTANDING BENEFITS
      {
        category: 'Understanding Benefits',
        question: 'What is the 50/50 custody presumption?',
        answer:
          'The "A New Start at Equality" bill establishes equal shared custody as the starting point in family court proceedings. Instead of custody being decided based on arguments and litigation, both parents begin with equal time and rights unless there are specific, proven reasons to adjust this arrangement.',
      },
      {
        category: 'Understanding Benefits',
        question: 'How does the "Time Taken Time Back" bill protect against false allegations?',
        answer:
          'This bill addresses the common tactic of false CPS or abuse allegations used to gain custody advantages. When allegations are proven false, the bill mandates restoration of lost custody time and implements consequences for those who make knowingly false claims.',
      },
      {
        category: 'Understanding Benefits',
        question: 'What does "Cameras in Courtrooms" accomplish?',
        answer:
          'Mandatory recording of family court proceedings creates transparency and accountability. Recordings protect both parents by documenting exactly what happens in court, preventing judicial misconduct, and providing a clear record for appeals.',
      },
      {
        category: 'Understanding Benefits',
        question: 'How does Child Support Dual Calculation Reform make the system fairer?',
        answer:
          'Current child support calculations often ignore the time and expenses of the non-custodial parent. Dual calculation reform ensures support payments reflect actual parenting time and shared expenses, creating more equitable financial arrangements.',
      },
      {
        category: 'Understanding Benefits',
        question: 'Why are temporary orders appealable important?',
        answer:
          'Temporary orders in family court often become permanent by default. The "Appealable Temporary Orders" bill gives parents the right to appeal interim decisions, ensuring proper due process and preventing unfair temporary arrangements from becoming permanent.',
      },

      // PLATFORM UPDATES
      {
        category: 'Platform Updates',
        question: 'Can I trust the bill information is accurate?',
        answer:
          'Yes. Our team actively monitors legislative developments and updates bill statuses in real-time. The Google Sheets system ensures information stays current, and any edits made by our staff appear almost immediately on the live site.',
      },
      {
        category: 'Platform Updates',
        question: 'What if I notice incorrect information on a bill page?',
        answer:
          'Please contact us immediately by emailing info@robertgarza.us. We monitor all submissions and can quickly correct any errors in our database.',
      },
      {
        category: 'Platform Updates',
        question: 'How can I stay informed about new bills or updates?',
        answer: (
          <>
            The website automatically displays the latest bill information whenever you visit. You can also{' '}
            <MuiLink href="/join-the-team" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              Join the Movement
            </MuiLink>{' '}
            to stay connected with the broader movement.
          </>
        ),
      },
    ],
    [],
  );

  // Generate FAQPage schema for rich results (only plain-text answers)
  const faqPageSchema = useMemo(() => {
    const plainTextFaqs: FAQInput[] = faqData
      .filter((faq) => typeof faq.answer === 'string')
      .map((faq) => ({
        question: faq.question,
        answer: faq.answer as string,
      }));
    return generateFAQPageSchema(plainTextFaqs);
  }, [faqData]);

  // Get unique categories while preserving order
  const categories = useMemo(() => {
    const uniqueCategories: string[] = [];
    faqData.forEach((faq) => {
      if (!uniqueCategories.includes(faq.category)) {
        uniqueCategories.push(faq.category);
      }
    });
    return uniqueCategories;
  }, [faqData]);

  // Initialize first question in each category as expanded
  React.useEffect(() => {
    const initialExpanded: { [key: string]: boolean } = {};
    categories.forEach((category) => {
      const firstQuestionIndex = faqData.findIndex((faq) => faq.category === category);
      if (firstQuestionIndex !== -1) {
        initialExpanded[`panel-${firstQuestionIndex}`] = true;
      }
    });
    setExpandedPanels(initialExpanded);
  }, [categories, faqData]);

  // Filter FAQs based on search query (searches both question and answer)
  const filteredFaqs = useMemo(() => {
    if (!searchQuery.trim()) return faqData;

    const query = searchQuery.toLowerCase();
    return faqData.filter((faq) => {
      const answerText = typeof faq.answer === 'string' ? faq.answer : '';
      return faq.question.toLowerCase().includes(query) || answerText.toLowerCase().includes(query);
    });
  }, [searchQuery, faqData]);

  // Group filtered FAQs by category
  const faqsByCategory = useMemo(() => {
    const grouped: { [key: string]: FAQItem[] } = {};
    filteredFaqs.forEach((faq) => {
      if (!grouped[faq.category]) {
        grouped[faq.category] = [];
      }
      grouped[faq.category].push(faq);
    });
    return grouped;
  }, [filteredFaqs]);

  // Handle accordion expansion
  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanels((prev) => ({
      ...prev,
      [panel]: isExpanded,
    }));

    // Track accordion expansions in Google Analytics
    if (isExpanded) {
      const faqIndex = parseInt(panel.replace('panel-', ''));
      const faq = faqData[faqIndex];
      trackEvent('faq_question_expand', 'faq', faq.question);
    }
  };

  // Handle search with analytics tracking
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);

    // Track search queries
    if (query.trim().length > 2) {
      trackEvent('faq_search', 'faq', query);
    }
  };

  // Track category anchor clicks
  const handleCategoryClick = (category: string) => {
    trackEvent('faq_category_filter', 'faq', category);
  };

  // Highlight matching text in search results
  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark
              key={index}
              style={{
                backgroundColor: '#ffeb3b',
                fontWeight: 'bold',
                padding: '0 2px',
              }}
            >
              {part}
            </mark>
          ) : (
            part
          ),
        )}
      </>
    );
  };

  return (
    <>
      <SEO
        title="RobertGarza.us Family Court Reform - FAQ"
        description="Find answers about accessing family court reform bills, emailing legislators, and using advocacy tools. Learn how to use RobertGarza.us to advance parental equality and judicial accountability."
        path="/faq"
        schema={faqPageSchema}
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
            Frequently Asked Questions
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Everything you need to know about accessing bills, contacting legislators, and advancing family court reform
          </Typography>
        </Box>

        {/* Sticky Search Bar */}
        <Box
          sx={{
            position: 'sticky',
            top: { xs: 56, sm: 64 }, // Account for AppBar height
            zIndex: 10,
            backgroundColor: 'background.default',
            pb: 2,
            mb: 2,
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search questions and answers..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'background.paper',
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
          {searchQuery && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Found {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        {/* Table of Contents */}
        {!searchQuery && (
          <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Quick Navigation
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 2 }}>
              {categories.map((category) => (
                <MuiLink
                  key={category}
                  href={`#${category.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => handleCategoryClick(category)}
                  sx={{
                    textDecoration: 'none',
                    color: 'primary.main',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {category}
                </MuiLink>
              ))}
            </Box>
          </Paper>
        )}

        {/* FAQ Accordions by Category */}
        {categories.map((category) => {
          const categoryFaqs = faqsByCategory[category];

          // Hide category if no matching FAQs
          if (!categoryFaqs || categoryFaqs.length === 0) {
            return null;
          }

          return (
            <Box key={category} id={category.toLowerCase().replace(/\s+/g, '-')} sx={{ mb: 4 }}>
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.main',
                  mb: 2,
                  pt: 2,
                }}
              >
                {category}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {categoryFaqs.map((faq, index) => {
                const panelId = `panel-${faqData.indexOf(faq)}`;
                return (
                  <Accordion
                    key={panelId}
                    expanded={expandedPanels[panelId] || false}
                    onChange={handleAccordionChange(panelId)}
                    sx={{
                      mb: 1,
                      '&:before': {
                        display: 'none',
                      },
                      boxShadow: 1,
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`${panelId}-content`}
                      id={`${panelId}-header`}
                    >
                      <Typography fontWeight="medium">{highlightText(faq.question, searchQuery)}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography color="text.secondary">
                        {typeof faq.answer === 'string' ? highlightText(faq.answer, searchQuery) : faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          );
        })}

        {/* No Results Message */}
        {filteredFaqs.length === 0 && searchQuery && (
          <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              No results found
            </Typography>
            <Typography color="text.secondary">
              Try different keywords or browse all questions by clearing the search
            </Typography>
          </Paper>
        )}
      </Container>
    </>
  );
};

export default FAQPage;
